import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, KeyRound } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { activateAccount, loadInvitation } from './api';
import AuthFrame from './AuthFrame';
import { Field, errorMessage, fieldAria } from './ui';
import { tx } from './i18n';
import { roleLabel } from './labels';

// Where an invitation email lands. Public by necessity -- the person has no
// account password yet, and the token in the URL is the only credential.
//
// The invitation is fetched before anything is typed so the page can show who
// it is for. Somebody who opened a link forwarded to the wrong person should
// see that immediately, not after choosing a password.
export default function XomicsActivate() {
  const { token } = useParams();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const passwordChecks = [
    password.length >= 12,
    /[a-z]/.test(password),
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setInvitation(await loadInvitation(token));
      setLoadError('');
    } catch (requestError) {
      setLoadError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  async function submit(event) {
    event.preventDefault();
    setError('');
    // Checked here as well as on the server, because getting it wrong after
    // typing a long password twice is a miserable way to find out.
    if (password !== confirmation) {
      setError(tx("ui.activate.the_two_passwords_do_not_match_d9ae819c"));
      return;
    }
    setSubmitting(true);
    try {
      await activateAccount(token, password);
      setDone(true);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <AuthFrame>
        <div className="xo-loader" aria-label={tx("ui.activate.checking_your_invitation_2f2b9395")} />
      </AuthFrame>
    );
  }

  if (loadError) {
    return (
      <AuthFrame>
        <div className="xo-auth-card-head">
          <p className="xo-eyebrow">{tx("ui.activate.phenome_xomics_270f14e1")}</p>
          <h1>{tx("ui.activate.this_link_is_not_valid_047ed160")}</h1>
        </div>
        <div className="xo-auth-message is-error">
          <p className="xo-muted">{loadError}</p>
          <p className="xo-muted">
            {tx("ui.activate.an_invitation_link_works_once_and_expires_after_72_hou_2c567096")}
          </p>
        </div>
        <Link className="xo-btn xo-auth-submit" to="/xomics/login">
          {tx("ui.activate.go_to_sign_in_726b0f6d")}
        </Link>
      </AuthFrame>
    );
  }

  if (done) {
    return (
      <AuthFrame>
        <div className="xo-auth-complete"><CheckCircle2 aria-hidden="true" /></div>
        <div className="xo-auth-card-head">
          <p className="xo-eyebrow">{tx("ui.activate.phenome_xomics_270f14e1")}</p>
          <h1>{tx("ui.activate.your_account_is_ready_9801e167")}</h1>
          <p className="xo-muted">
            {tx("ui.activate.sign_in_as_5d2ba59a")} <strong>{invitation.email}</strong>.
          </p>
        </div>
        <Link className="xo-btn xo-auth-submit" to="/xomics/login">
          {tx("ui.activate.sign_in_ada2e9e9")}
        </Link>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <div className="xo-auth-card-head">
        <span className="xo-auth-card-icon"><KeyRound aria-hidden="true" /></span>
        <p className="xo-eyebrow">{tx("ui.activate.phenome_xomics_270f14e1")}</p>
        <h1>{tx("ui.activate.set_your_password_4f9f9c13")}</h1>
        <p className="xo-muted">
          {invitation.firstName}{tx("ui.activate.your_account_fe897140")} <strong>{invitation.email}</strong> {tx("ui.activate.has_been_created_as_fe4c371e")} {roleLabel(invitation.role.code)}{tx("ui.activate.choose_a_password_to_finish_be987c3e")}
        </p>
      </div>

        <form onSubmit={submit} className="xo-form xo-auth-form">
          <Field
            id="activate-password"
            label={tx("ui.activate.password_8be3c943")}
            hint={tx("ui.activate.at_least_12_characters_with_upper_and_lower_case_a_num_31fd2e97")}
          >
            <input
              {...fieldAria('activate-password', { hint: 'x' })}
              type="password"
              autoComplete="new-password"
              value={password}
              disabled={submitting}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <div className="xo-password-meter"
            aria-label={tx("ui.activate.at_least_12_characters_with_upper_and_lower_case_a_num_31fd2e97")}>
            <div aria-hidden="true">
              {passwordChecks.map((passed, index) => (
                <span key={index} className={passed ? 'is-passed' : ''} />
              ))}
            </div>
            <small>{tx("ui.activate.at_least_12_characters_with_upper_and_lower_case_a_num_31fd2e97")}</small>
          </div>

          <Field id="activate-confirm" label={tx("ui.activate.password_again_84296be1")}>
            <input
              {...fieldAria('activate-confirm')}
              type="password"
              autoComplete="new-password"
              value={confirmation}
              disabled={submitting}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </Field>

          {error && <p className="xo-alert t-bad" role="alert">{error}</p>}

          <button type="submit" className="xo-btn xo-auth-submit"
            disabled={submitting || passwordChecks.some((passed) => !passed) || !confirmation}>
            {submitting ? tx("ui.activate.setting_your_password_9fdde855") : tx("ui.activate.set_password_94408e41")}
          </button>
        </form>
    </AuthFrame>
  );
}
